import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useId, useMemo, useState } from 'react';

import { Button } from '../../shared/ui/Button';
import { ConfirmInline } from '../../shared/ui/ConfirmInline';
import { IconButton } from '../../shared/ui/IconButton';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { Select } from '../../shared/ui/Select';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import {
  ACTIVITY_KEYS,
  deleteEventType,
  listEventTypes,
  setEventTypeArchived,
  upsertEventType,
  type EventType,
} from './activity-api';
import {
  categoryLabel,
  EVENT_CATEGORIES,
  EVENT_COLORS,
  type EventCategory,
  type EventColor,
} from './activity-format';
import { EventIcon, ICON_CHOICES } from './EventIcon';
import s from './EventTypesPanel.module.css';

interface Props {
  instituteId: string;
  /** Owner/admin. The RPCs enforce this too; this only hides dead controls. */
  canEdit: boolean;
}

interface Draft {
  id: string | null;
  label: string;
  category: EventCategory;
  color: EventColor;
  icon: string;
  amount_direction: 'none' | 'credit' | 'debit';
  requires_amount: boolean;
  requires_follow_up: boolean;
  default_visible_to_student: boolean;
  sort_order: number;
}

const NEW_DRAFT: Draft = {
  id: null,
  label: '',
  category: 'admin',
  color: 'slate',
  icon: 'Circle',
  amount_direction: 'none',
  requires_amount: false,
  requires_follow_up: false,
  default_visible_to_student: false,
  sort_order: 900,
};

const COLOR_CLASS: Record<EventColor, string> = {
  slate: s.slate ?? '',
  teal: s.teal ?? '',
  blue: s.blue ?? '',
  green: s.green ?? '',
  amber: s.amber ?? '',
  rose: s.rose ?? '',
  violet: s.violet ?? '',
};

export function EventTypesPanel({ instituteId, canEdit }: Props): JSX.Element {
  const fid = useId();
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: [...ACTIVITY_KEYS.eventTypes(instituteId), showArchived],
    queryFn: () => listEventTypes(instituteId, showArchived),
    enabled: instituteId !== '',
    staleTime: 60 * 1000,
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ACTIVITY_KEYS.eventTypes(instituteId) });
  }, [queryClient, instituteId]);

  const save = useMutation({
    mutationFn: (d: Draft) =>
      upsertEventType({
        institute_id: instituteId,
        id: d.id,
        label: d.label.trim(),
        category: d.category,
        color: d.color,
        icon: d.icon === '' ? null : d.icon,
        amount_direction: d.amount_direction,
        requires_amount: d.requires_amount,
        requires_follow_up: d.requires_follow_up,
        default_visible_to_student: d.default_visible_to_student,
        sort_order: d.sort_order,
      }),
    onSuccess: () => {
      setDraft(null);
      setError(null);
      refresh();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const archive = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      setEventTypeArchived(id, archived),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteEventType(id),
    onSuccess: () => {
      setConfirmId(null);
      setError(null);
      refresh();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const grouped = useMemo(() => {
    const rows = query.data ?? [];
    const byCategory = new Map<EventCategory, EventType[]>();
    for (const t of rows) {
      const bucket = byCategory.get(t.category);
      if (bucket === undefined) byCategory.set(t.category, [t]);
      else bucket.push(t);
    }
    return EVENT_CATEGORIES.map((c) => ({ category: c, items: byCategory.get(c) ?? [] })).filter(
      (g) => g.items.length > 0,
    );
  }, [query.data]);

  function submit(event: React.FormEvent): void {
    event.preventDefault();
    if (draft === null) return;
    if (draft.label.trim() === '') {
      setError('Give the type a name.');
      return;
    }
    setError(null);
    save.mutate(draft);
  }

  return (
    <div className={s.root}>
      <div className={s.head}>
        <p className={s.intro}>
          What staff can log against a student. The built-in set covers a full IELTS course and a
          consultancy pipeline — rename, recolour or archive anything that does not match how you
          work, and add your own.
        </p>
        <div className={s.headActions}>
          <label className={s.toggle} htmlFor={`${fid}-arch`}>
            <input
              id={`${fid}-arch`}
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
              }}
            />
            Show archived
          </label>
          {canEdit ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setDraft({ ...NEW_DRAFT });
              }}
            >
              <Plus size={15} aria-hidden="true" />
              New type
            </Button>
          ) : null}
        </div>
      </div>

      {error !== null ? <StatusBanner tone="error">{error}</StatusBanner> : null}
      {query.isError ? (
        <StatusBanner tone="error">Could not load the event types.</StatusBanner>
      ) : null}

      {draft !== null ? (
        <form className={s.editor} onSubmit={submit}>
          <div className={s.editorRow}>
            <div className={s.field}>
              <Label htmlFor={`${fid}-label`}>Name</Label>
              <Input
                id={`${fid}-label`}
                value={draft.label}
                maxLength={60}
                placeholder="Scholarship approved"
                onChange={(e) => {
                  setDraft({ ...draft, label: e.target.value });
                }}
              />
            </div>
            <div className={s.field}>
              <Label htmlFor={`${fid}-cat`}>Group</Label>
              <Select
                id={`${fid}-cat`}
                value={draft.category}
                onChange={(e) => {
                  setDraft({ ...draft, category: e.target.value as EventCategory });
                }}
              >
                {EVENT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {categoryLabel(c)}
                  </option>
                ))}
              </Select>
            </div>
            <div className={s.field}>
              <Label htmlFor={`${fid}-icon`}>Icon</Label>
              <Select
                id={`${fid}-icon`}
                value={draft.icon}
                onChange={(e) => {
                  setDraft({ ...draft, icon: e.target.value });
                }}
              >
                <option value="">None</option>
                {ICON_CHOICES.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className={s.field}>
            <span className={s.swatchLabel}>Colour</span>
            <div className={s.swatches} role="radiogroup" aria-label="Colour">
              {EVENT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  role="radio"
                  aria-checked={draft.color === color}
                  aria-label={color}
                  className={`${s.swatch ?? ''} ${
                    // eslint-disable-next-line security/detect-object-injection -- `color` iterates the EVENT_COLORS literal tuple, not user input
                    COLOR_CLASS[color]
                  } ${draft.color === color ? (s.swatchOn ?? '') : ''}`.trim()}
                  onClick={() => {
                    setDraft({ ...draft, color });
                  }}
                >
                  <EventIcon name={draft.icon === '' ? null : draft.icon} />
                </button>
              ))}
            </div>
          </div>

          <div className={s.field}>
            <Label htmlFor={`${fid}-dir`}>Effect on the student’s balance</Label>
            <Select
              id={`${fid}-dir`}
              value={draft.amount_direction}
              onChange={(e) => {
                setDraft({
                  ...draft,
                  amount_direction: e.target.value as Draft['amount_direction'],
                });
              }}
            >
              <option value="none">None — not a money entry</option>
              <option value="debit">Adds to what they owe (a charge)</option>
              <option value="credit">Reduces what they owe (a payment)</option>
            </Select>
          </div>

          <div className={s.checks}>
            <label className={s.toggle} htmlFor={`${fid}-amt`}>
              <input
                id={`${fid}-amt`}
                type="checkbox"
                checked={draft.requires_amount}
                onChange={(e) => {
                  setDraft({ ...draft, requires_amount: e.target.checked });
                }}
              />
              Always ask for an amount
            </label>
            <label className={s.toggle} htmlFor={`${fid}-fu`}>
              <input
                id={`${fid}-fu`}
                type="checkbox"
                checked={draft.requires_follow_up}
                onChange={(e) => {
                  setDraft({ ...draft, requires_follow_up: e.target.checked });
                }}
              />
              Always ask for a follow-up date
            </label>
            <label className={s.toggle} htmlFor={`${fid}-vis`}>
              <input
                id={`${fid}-vis`}
                type="checkbox"
                checked={draft.default_visible_to_student}
                onChange={(e) => {
                  setDraft({ ...draft, default_visible_to_student: e.target.checked });
                }}
              />
              Show to the student by default
            </label>
          </div>

          <div className={s.editorActions}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setDraft(null);
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending}>
              {draft.id === null ? 'Add type' : 'Save'}
            </Button>
          </div>
        </form>
      ) : null}

      {query.isPending ? (
        <p className={s.loading}>Loading…</p>
      ) : (
        grouped.map((group) => (
          <section key={group.category} className={s.group}>
            <h3 className={s.groupLabel}>{categoryLabel(group.category)}</h3>
            <ul className={s.list}>
              {group.items.map((t) => (
                <li
                  key={t.id}
                  className={`${s.row ?? ''} ${t.archived_at !== null ? (s.rowArchived ?? '') : ''}`.trim()}
                >
                  <span
                    className={`${s.dot ?? ''} ${
                      // eslint-disable-next-line security/detect-object-injection -- `color` is a literal union constrained by the DB CHECK, not user input
                      COLOR_CLASS[t.color]
                    }`.trim()}
                    aria-hidden="true"
                  >
                    <EventIcon name={t.icon} />
                  </span>

                  <span className={s.rowMain}>
                    <span className={s.rowLabel}>{t.label}</span>
                    <span className={s.rowMeta}>
                      {t.is_system ? 'Built-in' : 'Custom'}
                      {t.amount_direction === 'credit' ? ' · payment' : ''}
                      {t.amount_direction === 'debit' ? ' · charge' : ''}
                      {t.requires_follow_up ? ' · needs follow-up' : ''}
                      {t.default_visible_to_student ? ' · shown to student' : ''}
                      {t.usage_count > 0 ? ` · used ${String(t.usage_count)}×` : ''}
                      {t.archived_at !== null ? ' · archived' : ''}
                    </span>
                  </span>

                  {canEdit ? (
                    <span className={s.rowActions}>
                      <IconButton
                        label={`Edit ${t.label}`}
                        onClick={() => {
                          setDraft({
                            id: t.id,
                            label: t.label,
                            category: t.category,
                            color: t.color,
                            icon: t.icon ?? '',
                            amount_direction: t.amount_direction,
                            requires_amount: t.requires_amount,
                            requires_follow_up: t.requires_follow_up,
                            default_visible_to_student: t.default_visible_to_student,
                            sort_order: t.sort_order,
                          });
                        }}
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </IconButton>
                      <IconButton
                        label={t.archived_at === null ? `Archive ${t.label}` : `Restore ${t.label}`}
                        onClick={() => {
                          archive.mutate({ id: t.id, archived: t.archived_at === null });
                        }}
                      >
                        {t.archived_at === null ? (
                          <Archive size={14} aria-hidden="true" />
                        ) : (
                          <ArchiveRestore size={14} aria-hidden="true" />
                        )}
                      </IconButton>
                      {/* Deleting is only ever offered for a custom type nothing
                          has been logged against — anything else must be
                          archived so existing history keeps its label. */}
                      {!t.is_system && t.usage_count === 0 ? (
                        <IconButton
                          label={`Delete ${t.label}`}
                          tone="danger"
                          onClick={() => {
                            setConfirmId(confirmId === t.id ? null : t.id);
                          }}
                        >
                          <Trash2 size={14} aria-hidden="true" />
                        </IconButton>
                      ) : null}
                    </span>
                  ) : null}

                  {confirmId === t.id ? (
                    <ConfirmInline
                      message={`Delete “${t.label}” permanently?`}
                      confirmLabel="Delete"
                      busy={remove.isPending}
                      onCancel={() => {
                        setConfirmId(null);
                      }}
                      onConfirm={() => {
                        remove.mutate(t.id);
                      }}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
